import type { ICol } from "src/interfaces";
import { v4 as uuidv4 } from "uuid";

import { unregisterRowFormFields } from "../collectActiveFormFieldNames";
import { replaceExclusiveValues } from "./replaceExclusiveValues";

/**
 * Flattens a raw content grid into a fresh cols object,
 * assigning each cell a new UUID and remapping shared `exclusive` group ids
 * so this instance doesn't collide with any other instance of the same template.
 */
export const generateContentCols = (
  rawContent: unknown,
): Record<string, ICol> => {
  let colsWithIds: Record<string, ICol> = {};

  const toUuid = (data: any, index?: number): void => {
    if (Array.isArray(data)) {
      if (index === undefined) {
        data.forEach((row, i) => toUuid(row, i));
        return;
      }
      data.forEach((col, i) => {
        toUuid({ ...col, gridRow: index, gridCol: i }, index);
      });
      return;
    }

    const item = {
      gridRow: data.gridRow ?? index ?? 0,
      gridCol: data.gridCol ?? 0,
      ...data,
    };

    colsWithIds = { ...colsWithIds, [uuidv4()]: item };
  };

  toUuid(rawContent);
  return replaceExclusiveValues(colsWithIds);
};

/**
 * Replaces a cell's nested `content.cols` with a freshly generated set built
 * from `rawContent`, unregistering the cell's previous nested field
 * registrations first. Without the unregister step, react-hook-form keeps the
 * old fields alive under their old UUIDs; if they carried an `exclusive`
 * group id, the ghost sticks around sharing that id with the new fields and
 * breaks the group's required-together validation.
 */
export const replaceCellContent = (
  oldCellId: string | undefined,
  oldCell: ICol | undefined,
  rawContent: unknown,
  formValues: Record<string, unknown>,
  unregister: (name: string) => void,
): { cols: Record<string, ICol>; flex: string } => {
  if (oldCellId && oldCell) {
    unregisterRowFormFields(
      formValues,
      { cols: { [oldCellId]: oldCell } },
      unregister,
    );
  }

  return { cols: generateContentCols(rawContent), flex: "" };
};
